// src/components/MediaViewer.js
// Full-size viewer for a drop or a free clip. Opened by clicking a card in
// My Drops or on Home — both showed `cursor: pointer` long before anything
// actually opened.
//
// The media is shown whole (`object-fit: contain`), never cropped: the cards
// crop to fit their row, so this is the one place you see the real frame.
// Arrow keys and the on-screen chevrons move through the same list the card
// came from, so a set can be browsed without closing and reopening.

import { useCallback, useEffect, useState } from "react";

const kindOf = (item) => item.type || item.kind || "image";

// Videos play from `url`; images prefer the full asset and fall back to the
// thumbnail (free clips only ever carry a thumbnail until they are imported).
const srcOf = (item) =>
  kindOf(item) === "video" ? item.url || item.thumbnail_url : item.url || item.thumbnail_url;

export default function MediaViewer({ items = [], index = 0, onClose, onNav, onRecreate }) {
  const [failed, setFailed] = useState(false);
  const item = items[index];

  const go = useCallback(
    (delta) => {
      if (!items.length || !onNav) return;
      const next = (index + delta + items.length) % items.length;
      setFailed(false);
      onNav(next);
    },
    [index, items.length, onNav]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    // The page behind must not scroll while this is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, go]);

  useEffect(() => setFailed(false), [index]);

  if (!item) return null;

  const kind = kindOf(item);
  const src = srcOf(item);
  const many = items.length > 1 && !!onNav;

  return (
    <div className="viewer" onClick={onClose} role="dialog" aria-modal="true">
      <button className="viewer__close" onClick={onClose} aria-label="Close">✕</button>

      {many && (
        <button
          className="viewer__nav viewer__nav--prev"
          onClick={(e) => { e.stopPropagation(); go(-1); }}
          aria-label="Previous"
        >‹</button>
      )}

      <div className="viewer__stage" onClick={(e) => e.stopPropagation()}>
        {failed || !src ? (
          <div className="viewer__failed">
            <p>This file can’t be played.</p>
            <span>Its source is missing, so there is nothing to show.</span>
          </div>
        ) : kind === "video" ? (
          <video src={src} controls autoPlay playsInline onError={() => setFailed(true)} />
        ) : (
          <img src={src} alt={item.prompt || ""} onError={() => setFailed(true)} />
        )}

        {(item.prompt || item.creator || onRecreate) && (
          <div className="viewer__bar">
            <div className="viewer__meta">
              {item.prompt && <p>{item.prompt}</p>}
              {item.creator && <span>{item.creator}</span>}
            </div>
            {onRecreate && (
              <button className="btn btn--primary" onClick={() => onRecreate(item)}>
                Recreate →
              </button>
            )}
          </div>
        )}
      </div>

      {many && (
        <button
          className="viewer__nav viewer__nav--next"
          onClick={(e) => { e.stopPropagation(); go(1); }}
          aria-label="Next"
        >›</button>
      )}

      {many && <span className="viewer__count">{index + 1} / {items.length}</span>}
    </div>
  );
}
